import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { ExcelButtons } from '../../components/ExcelButtons';

interface BadgeDto {
  id: string;
  name: string;
  description?: string | null;
  translations?: Record<string, Record<string, string>> | null;
  imageUrl?: string | null;
  priceCoins: number;
  category?: string | null;
  tier?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

export const BadgeList: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  const { tableProps } = useTable<BadgeDto>({
    resource: 'badges',
    filters: {
      permanent: [{ field: 'search', operator: 'contains', value: searchText }],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="badges">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <ExcelButtons resource="admin/challenge/badges" />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column
          dataIndex="category"
          title="Category"
          filters={[
            { text: 'fitness', value: 'fitness' },
            { text: 'nutrition', value: 'nutrition' },
            { text: 'streak', value: 'streak' },
          ]}
          onFilter={(value, record: any) => record.category === value}
          render={(cat: string | null) => (cat ? <Tag style={tagStyle('#1890ff')}>{cat}</Tag> : '—')}
        />
        <Table.Column
          dataIndex="tier"
          title="Tier"
          filters={[
            { text: 'bronze', value: 'bronze' },
            { text: 'silver', value: 'silver' },
            { text: 'gold', value: 'gold' },
          ]}
          onFilter={(value, record: any) => record.tier === value}
          render={(tier: string | null) => {
            const tierColor = tier === 'gold' ? '#f5a623' : tier === 'silver' ? '#bfbfbf' : tier === 'bronze' ? '#d48806' : '#898989';
            return tier ? <Tag style={tagStyle(tierColor)}>{tier}</Tag> : '—';
          }}
        />
        <Table.Column dataIndex="priceCoins" title="Price (Coins)" />
        <Table.Column dataIndex="sortOrder" title="Sort Order" />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          filters={[
            { text: 'Active', value: true },
            { text: 'Inactive', value: false },
          ]}
          onFilter={(value, record: any) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: any, record: BadgeDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="badges" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="badges" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="badges" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
